import * as React from 'react';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgService } from 'services/org.service';
import { aiProductService } from 'services/ai-product.service';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { LiaJira } from 'react-icons/lia';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { PRODUCT_TYPES } from 'lib/constants/compliance';
import { useAuth } from 'contexts/auth-context';

export default function IconBreadcrumbs() {
  const VISIBLE_BREADCRUMB_COUNT = 4;
  const { user: authUser } = useAuth();

  const location = useLocation();
  const match = location.pathname.match(/\/compliance\/([^/]+)/);
  const orgId = match?.[1];
  const [searchParams] = useSearchParams();
  const prId = searchParams.get('prId');

  const { data: orgHierarchy = [] } = useQuery({
    queryKey: ['organizations', orgId, 'orgHierarchy'],
    queryFn: () => orgService.getOrgHierarchy(orgId),
    enabled: !!orgId
  });

  const { data: productData } = useQuery({
    queryKey: ['product', prId],
    queryFn: () => aiProductService.getById(prId),
    enabled: !!prId
  });

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Trim the hierarchy to start from authUser's orgId
  let visibleOrgHierarchy = orgHierarchy;
  const index = orgHierarchy.findIndex((org) => org._id === authUser.orgId);
  if (index !== -1) {
    visibleOrgHierarchy = orgHierarchy.slice(index);
  }

  const total = visibleOrgHierarchy.length;
  const root = visibleOrgHierarchy[0];
  const second = visibleOrgHierarchy[1];
  const leaf = visibleOrgHierarchy[total - 1];
  const lastOrgIdToHighlight = prId ? null : leaf?._id;

  let menuMiddle = [];
  let visibleMiddle = [];

  if (total > VISIBLE_BREADCRUMB_COUNT) {
    const visibleCount = VISIBLE_BREADCRUMB_COUNT - 3;
    const startIdx = total - 1 - visibleCount;
    menuMiddle = visibleOrgHierarchy.slice(2, startIdx);
    visibleMiddle = visibleOrgHierarchy.slice(startIdx, total - 1);
  } else {
    visibleMiddle = visibleOrgHierarchy.slice(2, total - 1);
  }

  return (
    <Breadcrumbs aria-label="breadcrumb">
      {/* Root */}
      {root && (
        <NavLink
          to={`/compliance/${root._id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: root._id === lastOrgIdToHighlight ? 'bold' : 'normal'
          }}
        >
          <CorporateFareIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {root.name}
        </NavLink>
      )}

      {/* Second Org */}
      {second && (
        <NavLink
          to={`/compliance/${second._id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: second._id === lastOrgIdToHighlight ? 'bold' : 'normal'
          }}
        >
          <CorporateFareIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {second.name}
        </NavLink>
      )}

      {/* Collapsed middle */}
      {menuMiddle.length > 0 && (
        <>
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {menuMiddle.map((org) => (
              <MenuItem
                key={org._id}
                component={NavLink}
                to={`/compliance/${org._id}`}
                onClick={handleMenuClose}
              >
                {org.name}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Inline visible middle orgs */}
      {visibleMiddle.map((org) => (
        <NavLink
          key={org._id}
          to={`/compliance/${org._id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: org._id === lastOrgIdToHighlight ? 'bold' : 'normal'
          }}
        >
          <CorporateFareIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {org.name}
        </NavLink>
      ))}

      {/* Leaf org (if not already shown) */}
      {leaf && ![root?._id, second?._id, ...visibleMiddle.map((o) => o._id)].includes(leaf._id) && (
        <NavLink
          to={`/compliance/${leaf._id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: leaf._id === lastOrgIdToHighlight ? 'bold' : 'normal'
          }}
        >
          <CorporateFareIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {leaf.name}
        </NavLink>
      )}

      {/* Product */}
      {prId && (
        <Typography sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}>
          {productData?.type === PRODUCT_TYPES.JIRA ? (
            <LiaJira style={{ marginRight: '0.2rem' }} />
          ) : productData?.type === PRODUCT_TYPES.INTERNAL ? (
            <ArchiveOutlinedIcon fontSize="inherit" sx={{ mr: '0.2rem' }} />
          ) : (
            <Inventory2OutlinedIcon fontSize="inherit" sx={{ mr: '0.2rem' }} />
          )}
          {productData?.product_name}
        </Typography>
      )}
    </Breadcrumbs>
  );
}
